import type { FC } from 'react'
import { lazy, Suspense, useEffect } from 'react'
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom'
import { LoaderBox, PrivateRoute } from '@/components/'
import { useAuth } from '@/hooks'
import { Layout } from '@/modules'
import { appStore, useAppSelector } from '@/store'

const Login = lazy(() =>
  import('@/pages').then(({ Login }) => ({
    default: Login,
  }))
)

const Dashboard = lazy(() =>
  import('@/pages').then(({ Dashboard }) => ({
    default: Dashboard,
  }))
)

const OrganizationList = lazy(() =>
  import('@/pages').then(({ OrganizationList }) => ({
    default: OrganizationList,
  }))
)

const Organization = lazy(() =>
  import('@/pages').then(({ Organization }) => ({
    default: Organization,
  }))
)

const Employee = lazy(() =>
  import('@/pages').then(({ Employee }) => ({
    default: Employee,
  }))
)

const MedComplexList = lazy(() =>
  import('@/pages').then(({ MedComplexList }) => ({
    default: MedComplexList,
  }))
)

const MedComplex = lazy(() =>
  import('@/pages').then(({ MedComplex }) => ({
    default: MedComplex,
  }))
)

const MedReports = lazy(() =>
  import('@/pages').then(({ MedReports }) => ({
    default: MedReports,
  }))
)

const Support = lazy(() =>
  import('@/pages').then(({ Support }) => ({
    default: Support,
  }))
)

const Development = lazy(() =>
  import('@/pages').then(({ Development }) => ({
    default: Development,
  }))
)

export enum ROUTE_PATH {
  BASE = '/',
  MAIN = '/main',
  LOGIN = '/login',
  DICTIONARIE_VEHICLE = '/dictionaries',
  ORGANIZATION = '/organization',
  ORGANIZATION_CREATE = '/organization/create/general',
  ORGANIZATION_EDIT = '/organization/:organizationId/:organizationTab',
  ORGANIZATION_EMPLOYEE_CREATE = '/organization/:organizationId/employee/create/general',
  ORGANIZATION_EMPLOYEE_EDIT = '/organization/:organizationId/employee/:employeeId/:employeeTab',
  MEDCOMPLEX = '/medcomplex',
  MEDCOMPLEX_CREATE = '/medcomplex/create/general',
  MEDCOMPLEX_EDIT = '/medcomplex/:medcomplexId/:medcomplexTab',
  // MEDCOMPLEX_EDIT = '/medcomplex/:medcomplexId',
  // MEDCOMPLEX_WATCH_LIST = '/medcomplex_watch',
  // MEDCOMPLEX_WATCH = '/medcomplex_watch/:medcomplexId',
  MEDREPORTS = '/reports/:tab/:curPage',
  HELP = '/help',
  DEVELOPMENT = '/development',
}

const BaseRoot = () => {
  const { employee } = useAppSelector(appStore)
  const navigate = useNavigate()

  useEffect(() => {
    if (employee && employee.roles.length > 0) {
      const defaultPath =
        // employee.roles[0]?.permissionFront[0]?.conf[0]?.url?.pathname ||
        ROUTE_PATH.MAIN
      navigate(defaultPath)
    }
  }, [employee, navigate])

  return <></>
}

export const AppRoutes: FC = () => {
  const { isAuthenticated } = useAuth()
  const { menuAccess } = useAppSelector(appStore)

  return (
    <BrowserRouter>
      {isAuthenticated &&
        menuAccess &&
        (menuAccess as { pathname: string }[]).filter(
          (m) => m.pathname !== '/development'
        ).length === 0 && (
          <Layout>
            <p>Нет доступных для отображения модулей</p>
          </Layout>
        )}
      <Routes>
        <Route
          path={ROUTE_PATH.BASE}
          element={
            isAuthenticated ? (
              <Suspense fallback={<LoaderBox />}>
                <BaseRoot />
              </Suspense>
            ) : (
              <Navigate to={ROUTE_PATH.LOGIN} />
            )
          }
        />
        <Route
          path={ROUTE_PATH.LOGIN}
          element={
            !isAuthenticated ? (
              <Suspense fallback={<LoaderBox />}>
                <Login />
              </Suspense>
            ) : (
              <Navigate to={ROUTE_PATH.BASE} />
            )
          }
        />
        <Route
          path={ROUTE_PATH.MAIN}
          element={
            <Suspense fallback={<LoaderBox />}>
              <PrivateRoute auth={{ isAuthenticated }} path={ROUTE_PATH.MAIN}>
                <Dashboard />
              </PrivateRoute>
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.ORGANIZATION}
          element={
            <Suspense fallback={<LoaderBox />}>
              <PrivateRoute
                auth={{ isAuthenticated }}
                path={ROUTE_PATH.ORGANIZATION}
              >
                <OrganizationList />
              </PrivateRoute>
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.ORGANIZATION_CREATE}
          element={
            <Suspense fallback={<LoaderBox />}>
              <PrivateRoute
                auth={{ isAuthenticated }}
                path={ROUTE_PATH.ORGANIZATION_CREATE}
              >
                <Organization />
              </PrivateRoute>
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.ORGANIZATION_EDIT}
          element={
            <Suspense fallback={<LoaderBox />}>
              <PrivateRoute
                auth={{ isAuthenticated }}
                path={ROUTE_PATH.ORGANIZATION_EDIT}
              >
                <Organization />
              </PrivateRoute>
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.ORGANIZATION_EMPLOYEE_CREATE}
          element={
            <Suspense fallback={<LoaderBox />}>
              <PrivateRoute
                auth={{ isAuthenticated }}
                path={ROUTE_PATH.ORGANIZATION_EMPLOYEE_CREATE}
              >
                <Employee />
              </PrivateRoute>
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.ORGANIZATION_EMPLOYEE_EDIT}
          element={
            <Suspense fallback={<LoaderBox />}>
              <PrivateRoute
                auth={{ isAuthenticated }}
                path={ROUTE_PATH.ORGANIZATION_EMPLOYEE_EDIT}
              >
                <Employee />
              </PrivateRoute>
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.MEDCOMPLEX}
          element={
            <Suspense fallback={<LoaderBox />}>
              <PrivateRoute
                auth={{ isAuthenticated }}
                path={ROUTE_PATH.MEDCOMPLEX}
              >
                <MedComplexList />
              </PrivateRoute>
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.MEDCOMPLEX_CREATE}
          element={
            <Suspense fallback={<LoaderBox />}>
              <PrivateRoute
                auth={{ isAuthenticated }}
                path={ROUTE_PATH.MEDCOMPLEX_CREATE}
              >
                <MedComplex />
              </PrivateRoute>
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.MEDCOMPLEX_EDIT}
          element={
            <Suspense fallback={<LoaderBox />}>
              <PrivateRoute
                auth={{ isAuthenticated }}
                path={ROUTE_PATH.MEDCOMPLEX_EDIT}
              >
                <MedComplex />
              </PrivateRoute>
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.MEDREPORTS}
          element={
            <Suspense fallback={<LoaderBox />}>
              <PrivateRoute
                auth={{ isAuthenticated }}
                path={ROUTE_PATH.MEDREPORTS}
              >
                <MedReports />
              </PrivateRoute>
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.HELP}
          element={
            <Suspense fallback={<LoaderBox />}>
              <PrivateRoute auth={{ isAuthenticated }} path={ROUTE_PATH.HELP}>
                <Support />
              </PrivateRoute>
            </Suspense>
          }
        />
        <Route
          path={ROUTE_PATH.DEVELOPMENT}
          element={
            <Suspense fallback={<LoaderBox />}>
              <PrivateRoute
                auth={{ isAuthenticated }}
                path={ROUTE_PATH.DEVELOPMENT}
              >
                <Development />
              </PrivateRoute>
            </Suspense>
          }
        />
      </Routes>
    </BrowserRouter>
  )
}
